import { isPlainObject } from "es-toolkit/compat";

/**
 * Given object with nested properties, returns a flat object with the nested
 * objects broken up by its "path" e.g.
 *   { a: 1, b: { c: 2, d: 3 } } => { a: 1, "b.c": 2, "b.d": 3}
 *
 * @returns the flat object representation of the provided object
 */
export const toFlattenedObject = <
  T extends Record<string, any>,
  X extends Record<string, any> = Record<
    string,
    string | number | boolean | Date | null
  >,
>(
  /** plain object with nested properties */
  obj: T,
  options: {
    /** do the nested properties need their keys prefixed? */
    prefix?: string;
    /** if set, calls the predicate on property's values that are plain objects. Return value cannot be undefined. */
    transformObjectValue?: (val: Record<string, any>) => any;
    /** if true, exclude properties where value is undefined */
    ignoreUndefined?: boolean;
  } = {
    prefix: "",
    ignoreUndefined: true,
  }
) => {
  const opts = Object.assign(
    {
      prefix: "",
      ignoreUndefined: true,
    },
    options
  );
  const result = {} as X;

  const recursiveFlatten = (obj: object, prefix = "") => {
    Object.entries(obj).forEach(([key, value]) => {
      if (
        (opts?.ignoreUndefined && value !== undefined) ||
        !opts?.ignoreUndefined
      ) {
        if (isPlainObject(value)) {
          if (
            opts.transformObjectValue &&
            typeof opts.transformObjectValue(value) !== "undefined"
          ) {
            // @ts-expect-error safe indexing error
            result[`${prefix ? `${prefix}.` : ""}${key}`] =
              opts.transformObjectValue(value);
          } else {
            recursiveFlatten(value, `${prefix ? `${prefix}.` : ""}${key}`);
          }
        } else {
          // @ts-expect-error safe indexing error
          result[`${prefix ? `${prefix}.` : ""}${key}`] = value;
        }
      }
    });
  };

  recursiveFlatten(obj, opts.prefix);

  return result;
};
